import { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash2, CheckCircle, DollarSign, FileText, Edit2, CreditCard, Smartphone, AlertCircle, Zap, Beaker, ChevronDown, Package, Minus } from 'lucide-react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { useModal } from './AppModal';
import PayInvoiceButton from './PayInvoiceButton';
import TerminalPaymentButton from './TerminalPaymentButton';
import { captureLocation } from './locationTracker';
import { useTheme } from './ThemeContext';

// Send auto-receipt email if enabled
async function sendAutoReceipt({ company, customer, amount, invoiceId, companyId, paymentMethod }) {
  if (!company?.auto_email_receipts) return;
  if (!customer?.email) return;
  try {
    await fetch('/api/send-receipt', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        invoiceId,
        companyId,
        customerEmail: customer.email,
        customerName: customer.first_name || customer.name || '',
        amount,
        companyName: company.name || '',
        paymentMethod
      })
    });
    return customer.email;
  } catch (err) {
    console.warn('[AutoReceipt] Failed to send:', err.message);
    return null;
  }
}

export default function JobCompletionModal({ job, companyId, onClose, onComplete, onNavigate }) {
  const { colors } = useTheme();
  const { showAlert, showError, showSuccess, showConfirm } = useModal();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [manualDepositCollected, setManualDepositCollected] = useState(false);
  const [step, setStep] = useState('review'); // 'review', 'materials', 'chemicals', 'signature', 'payment'
  const [jobItems, setJobItems] = useState([]);
  const [quote, setQuote] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [company, setCompany] = useState(null);
  const [invoice, setInvoice] = useState(null);

  // Inventory deduction state
  const [inventoryAutoDeduct, setInventoryAutoDeduct] = useState(true);
  const [inventoryEnabled, setInventoryEnabled] = useState(false);
  const [plannedMaterials, setPlannedMaterials] = useState([]);
  const [materialsUsed, setMaterialsUsed] = useState([]);
  const [jobPlanLocationId, setJobPlanLocationId] = useState(null);

  // Edit item state
  const [editingItem, setEditingItem] = useState(null);

  // Add item state
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemPrice, setNewItemPrice] = useState('');
  const [newItemQty, setNewItemQty] = useState(1);

  // Signature state
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [signatureMode, setSignatureMode] = useState('optional');

  // Auto-charge state
  const [autoChargeError, setAutoChargeError] = useState(null);
  const [autoCharging, setAutoCharging] = useState(false);

  // Quote fee credit state
  const [quoteFeeCredit, setQuoteFeeCredit] = useState(null);

  // Chemical tracking state
  const [chemicalTrackingEnabled, setChemicalTrackingEnabled] = useState(false);
  const [chemicalLibrary, setChemicalLibrary] = useState([]);
  const [jobChemicals, setJobChemicals] = useState([]);
  const [showAddChemical, setShowAddChemical] = useState(false);
  const [selectedChemicalId, setSelectedChemicalId] = useState('');
  const [chemicalQuantity, setChemicalQuantity] = useState('');
  const [chemicalUnit, setChemicalUnit] = useState('oz');
  const [chemicalArea, setChemicalArea] = useState('');
  const [chemicalTarget, setChemicalTarget] = useState('');
  const [chemicalMethod, setChemicalMethod] = useState('');
  const [chemicalDilution, setChemicalDilution] = useState('');

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    loadJobData();
  }, [job.id]);

  const loadJobData = async () => {
    try {
      setLoading(true);

      // Load company settings for signature mode and inventory settings
      const { data: companyData } = await supabaseData
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();

      setCompany(companyData);
      setSignatureMode(companyData?.completion_signature_mode || 'optional');
      setInventoryEnabled(companyData?.inventory_enabled || false);
      setInventoryAutoDeduct(companyData?.inventory_auto_deduct !== false); // Default true

      // Load customer
      const { data: customerData } = await supabaseData
        .from('customers')
        .select('*')
        .eq('id', job.customer_id)
        .single();
      setCustomer(customerData);

      // Load quote if linked
      let quoteData = null;
      let quoteItems = [];
      if (job.quote_id) {
        const { data: qData } = await supabaseData
          .from('quotes')
          .select('*')
          .eq('id', job.quote_id)
          .single();
        quoteData = qData;
        setQuote(qData);

        // Load quote items
        const { data: qItems } = await supabaseData
          .from('quote_items')
          .select('*')
          .eq('quote_id', job.quote_id)
          .order('created_at');
        quoteItems = qItems || [];
      }

      // Load existing job items
      const { data: existingItems } = await supabaseData
        .from('job_items')
        .select('*')
        .eq('job_id', job.id)
        .order('created_at');

      // If we have existing job items, use them
      if (existingItems && existingItems.length > 0) {
        setJobItems(existingItems);
      }
      // Otherwise, copy from quote items if available
      else if (quoteItems.length > 0) {
        const newItems = quoteItems.map(qi => ({
          id: `temp-${qi.id}`,
          job_id: job.id,
          sku_id: qi.sku_id,
          name: qi.name || qi.description,
          description: qi.description,
          quantity: qi.quantity,
          unit_price: qi.unit_price,
          total_price: qi.total_price || (qi.quantity * qi.unit_price),
          is_from_quote: true
        }));
        setJobItems(newItems);

        // Save these items to job_items table
        const itemsToInsert = newItems.map(item => ({
          job_id: job.id,
          sku_id: item.sku_id,
          name: item.name,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
          is_from_quote: true
        }));

        const { data: savedItems, error } = await supabaseData
          .from('job_items')
          .insert(itemsToInsert)
          .select();

        if (!error && savedItems) {
          setJobItems(savedItems);
        }
      }
      // If no quote, create a single item from job description/value
      else if (job.quoted_value || job.description) {
        const singleItem = {
          id: 'temp-single',
          job_id: job.id,
          name: job.description || job.job_type || 'Service',
          quantity: 1,
          unit_price: job.quoted_value || 0,
          total_price: job.quoted_value || 0
        };

        const { data: savedItem, error } = await supabaseData
          .from('job_items')
          .insert([{
            job_id: job.id,
            name: singleItem.name,
            quantity: 1,
            unit_price: singleItem.unit_price,
            total_price: singleItem.total_price
          }])
          .select()
          .single();

        if (!error && savedItem) {
          setJobItems([savedItem]);
        } else {
          setJobItems([singleItem]);
        }
      }

      // Load chemical tracking data if enabled
      if (companyData?.chemical_tracking_enabled) {
        setChemicalTrackingEnabled(true);

        // Load company's chemical library
        const { data: chemicals } = await supabaseData
          .from('chemicals')
          .select('*')
          .eq('company_id', companyId)
          .eq('is_active', true)
          .order('name');
        setChemicalLibrary(chemicals || []);

        // Load existing job chemicals
        const { data: existingChemicals } = await supabaseData
          .from('job_chemicals')
          .select('*')
          .eq('job_id', job.id)
          .order('created_at');
        setJobChemicals(existingChemicals || []);
      }

      // Check if this job has a quote fee credit from a quote visit
      if (job.quote_fee_credit_from_job_id) {
        const { data: quoteVisitJob } = await supabaseData
          .from('jobs')
          .select('quote_fee_amount')
          .eq('id', job.quote_fee_credit_from_job_id)
          .single();

        if (quoteVisitJob?.quote_fee_amount) {
          setQuoteFeeCredit(quoteVisitJob.quote_fee_amount);
        }
      }

      // Load job plan materials for inventory deduction
      if (companyData?.inventory_enabled) {
        // First try to load from job_plan
        const { data: jobPlan } = await supabaseData
          .from('job_plans')
          .select('*, job_plan_items(*)')
          .eq('job_id', job.id)
          .single();

        if (jobPlan && jobPlan.job_plan_items?.length > 0) {
          setJobPlanLocationId(jobPlan.stock_location_id);
          const materials = jobPlan.job_plan_items.map(item => ({
            id: item.id,
            inventory_item_id: item.inventory_item_id,
            name: item.name,
            quantity_planned: item.quantity || 1,
            quantity_used: item.quantity || 1
          }));
          setPlannedMaterials(materials);
          setMaterialsUsed(materials);
        }
        // If auto-deduct is on and no job_plan, build materials from job_items → SKU → inventory link
        else if (companyData?.inventory_auto_deduct !== false) {
          // Get default location for deduction
          const { data: defaultLoc } = await supabaseData
            .from('locations')
            .select('id')
            .eq('company_id', companyId)
            .eq('is_default', true)
            .single();

          if (defaultLoc) {
            setJobPlanLocationId(defaultLoc.id);

            // Find inventory items linked to the SKUs used in this job
            const jobSkuIds = (existingItems || [])
              .filter(item => item.sku_id)
              .map(item => item.sku_id);

            if (jobSkuIds.length > 0) {
              const { data: linkedInventory } = await supabaseData
                .from('inventory_items')
                .select('id, sku_id, name')
                .eq('company_id', companyId)
                .eq('is_active', true)
                .in('sku_id', jobSkuIds);

              if (linkedInventory && linkedInventory.length > 0) {
                const invMap = {};
                linkedInventory.forEach(inv => { invMap[inv.sku_id] = inv; });

                const autoMaterials = (existingItems || [])
                  .filter(item => item.sku_id && invMap[item.sku_id])
                  .map(item => ({
                    id: `auto-${item.id}`,
                    inventory_item_id: invMap[item.sku_id].id,
                    name: item.name || invMap[item.sku_id].name,
                    quantity_planned: item.quantity || 1,
                    quantity_used: item.quantity || 1
                  }));

                if (autoMaterials.length > 0) {
                  setPlannedMaterials(autoMaterials);
                  setMaterialsUsed(autoMaterials);
                }
              }
            }
          }
        }
      }

    } catch (error) {
      showError('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  // Calculations
  const calculateSubtotal = () => jobItems.reduce((sum, item) => sum + (item.total_price || 0), 0);
  const getTaxRate = () => {
    // Check if tax is disabled at company level
    if (company?.tax_enabled === false) return 0;
    // Use quote tax rate if available (using ?? to handle 0 correctly)
    if (quote?.tax_rate !== undefined && quote?.tax_rate !== null) return quote.tax_rate;
    // Fall back to company tax rate, or 0 if not set
    return company?.tax_rate ?? 0;
  };
  const calculateTax = () => calculateSubtotal() * getTaxRate();
  const getQuoteFeeCredit = () => quoteFeeCredit || 0;
  const calculateTotal = () => calculateSubtotal() + calculateTax() - getQuoteFeeCredit();
  // The deposit dollar amount owed on this job (from the quote, or the job copy).
  const depositDueAmount = Number(
    (quote?.deposit_required ? quote?.deposit_amount : 0) ||
    (job?.deposit_required ? job?.deposit_amount : 0) ||
    0
  ) || 0;
  // Already collected online (webhook flipped deposit_paid on quote or job)?
  const depositAlreadyPaid = !!((quote?.deposit_required && quote?.deposit_paid) || job?.deposit_paid);
  const getDepositPaid = () => {
    if (depositAlreadyPaid) return depositDueAmount;
    if (manualDepositCollected) return depositDueAmount; // marked collected in-person
    return 0;
  };
  const calculateBalanceDue = () => Math.max(0, calculateTotal() - getDepositPaid());

  // Item management
  const addItem = async () => {
    if (!newItemName.trim() || parseFloat(newItemPrice) <= 0) {
      showAlert('Please enter a name and valid price');
      return;
    }

    const price = parseFloat(newItemPrice);
    const qty = parseInt(newItemQty) || 1;

    const { data, error } = await supabaseData
      .from('job_items')
      .insert([{
        job_id: job.id,
        name: newItemName.trim(),
        quantity: qty,
        unit_price: price,
        total_price: price * qty,
        is_additional: true
      }])
      .select()
      .single();

    if (!error && data) {
      setJobItems([...jobItems, data]);
      setNewItemName('');
      setNewItemPrice('');
      setNewItemQty(1);
      setShowAddItem(false);
    }
  };

  const updateItem = async (itemId, updates) => {
    const { error } = await supabaseData
      .from('job_items')
      .update(updates)
      .eq('id', itemId);

    if (!error) {
      setJobItems(jobItems.map(i => i.id === itemId ? { ...i, ...updates } : i));
      setEditingItem(null);
    }
  };

  const removeItem = async (itemId) => {
    const confirmed = await showConfirm('Remove this item?', { confirmStyle: 'danger' });
    if (!confirmed) return;

    await supabaseData.from('job_items').delete().eq('id', itemId);
    setJobItems(jobItems.filter(i => i.id !== itemId));
  };

  // Chemical management
  const addChemical = async () => {
    if (!selectedChemicalId && !chemicalQuantity) {
      showAlert('Please select a chemical and enter quantity');
      return;
    }

    const selectedChem = chemicalLibrary.find(c => c.id === selectedChemicalId);
    if (!selectedChem) {
      showAlert('Please select a chemical from your library');
      return;
    }

    const quantity = parseFloat(chemicalQuantity);
    if (isNaN(quantity) || quantity <= 0) {
      showAlert('Please enter a valid quantity');
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();

    const chemicalData = {
      job_id: job.id,
      chemical_id: selectedChem.id,
      chemical_name: selectedChem.name,
      epa_number: selectedChem.epa_number,
      manufacturer: selectedChem.manufacturer,
      quantity_used: quantity,
      unit: chemicalUnit || selectedChem.default_unit || 'oz',
      dilution_rate: chemicalDilution || null,
      application_area: chemicalArea || null,
      target_pest: chemicalTarget || null,
      application_method: chemicalMethod || null,
      technician_id: user?.id || null,
      applied_at: new Date().toISOString()
    };

    const { data, error } = await supabaseData
      .from('job_chemicals')
      .insert([chemicalData])
      .select()
      .single();

    if (!error && data) {
      setJobChemicals([...jobChemicals, data]);
      resetChemicalForm();
      setShowAddChemical(false);
    } else {
      showError('Failed to add chemical');
    }
  };

  const removeChemical = async (chemicalId) => {
    const confirmed = await showConfirm('Remove this chemical?', { confirmStyle: 'danger' });
    if (!confirmed) return;

    await supabaseData.from('job_chemicals').delete().eq('id', chemicalId);
    setJobChemicals(jobChemicals.filter(c => c.id !== chemicalId));
  };

  const resetChemicalForm = () => {
    setSelectedChemicalId('');
    setChemicalQuantity('');
    setChemicalUnit('oz');
    setChemicalArea('');
    setChemicalTarget('');
    setChemicalMethod('');
    setChemicalDilution('');
  };

  // Signature handling
  const initCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  useEffect(() => {
    if (step === 'signature' && canvasRef.current) {
      initCanvas();
    }
  }, [step]);

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    setIsDrawing(true);
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();

    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearSignature = () => {
    initCanvas();
    setHasSignature(false);
  };

  const getSignatureDataUrl = () => {
    return canvasRef.current?.toDataURL('image/png');
  };

  // Complete job and create invoice
  const completeJob = async () => {
    if (jobItems.length === 0) {
      showAlert('No items to invoice');
      return;
    }

    setSaving(true);
    try {
      // Generate invoice number
      const invoiceNumber = `INV-${Date.now().toString(36).toUpperCase()}`;

      // Get signature if captured
      let signatureUrl = null;
      if (hasSignature && canvasRef.current) {
        const dataUrl = getSignatureDataUrl();
        // Could upload to storage here, for now store as data URL
        signatureUrl = dataUrl;
      }

      // Create invoice
      const { data: invoiceData, error: invoiceError } = await supabaseData
        .from('invoices')
        .insert([{
          company_id: companyId,
          job_id: job.id,
          quote_id: job.quote_id,
          customer_id: job.customer_id,
          invoice_number: invoiceNumber,
          subtotal: calculateSubtotal(),
          tax_rate: getTaxRate(),
          tax_amount: calculateTax(),
          total: calculateTotal(),
          deposit_applied: getDepositPaid(),
          balance_due: calculateBalanceDue(),
          amount_paid: getDepositPaid(),
          status: calculateBalanceDue() <= 0 ? 'paid' : 'sent',
          due_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          completion_signature: signatureUrl
        }])
        .select()
        .single();

      if (invoiceError) throw invoiceError;

      // If the deposit was collected in-person during completion, record it on
      // the job (and originating quote) so the ledger reflects it everywhere.
      if (manualDepositCollected && !depositAlreadyPaid && depositDueAmount > 0) {
        const nowIso = new Date().toISOString();
        try {
          await supabaseData.from('jobs')
            .update({ deposit_paid: true, deposit_paid_at: nowIso })
            .eq('id', job.id);
          if (job.quote_id) {
            await supabaseData.from('quotes')
              .update({ deposit_paid: true, deposit_paid_at: nowIso })
              .eq('id', job.quote_id);
          }
        } catch (depErr) {
          console.error('Failed to persist manual deposit collection:', depErr);
        }
      }

      // Copy job items to invoice items
      const invoiceItems = jobItems.map(item => ({
        invoice_id: invoiceData.id,
        sku_id: item.sku_id,
        name: item.name,
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        total_price: item.total_price
      }));

      // Add quote fee credit line item if applicable
      if (quoteFeeCredit > 0) {
        invoiceItems.push({
          invoice_id: invoiceData.id,
          sku_id: null,
          name: 'Quote Visit Fee Credit',
          description: 'Credit applied from paid quote visit',
          quantity: 1,
          unit_price: -quoteFeeCredit,
          total_price: -quoteFeeCredit,
          is_quote_fee_credit: true
        });
      }

      await supabaseData.from('invoice_items').insert(invoiceItems);

      // Copy chemicals to invoice for permanent record
      if (jobChemicals.length > 0) {
        const invoiceChemicals = jobChemicals.map(chem => ({
          invoice_id: invoiceData.id,
          chemical_name: chem.chemical_name,
          epa_number: chem.epa_number,
          manufacturer: chem.manufacturer,
          quantity: chem.quantity_used,
          unit: chem.unit,
          dilution_rate: chem.dilution_rate,
          application_area: chem.application_area,
          target_pest: chem.target_pest,
          application_method: chem.application_method
        }));
        await supabaseData.from('invoice_chemicals').insert(invoiceChemicals);
      }

      // Deduct inventory if enabled (either auto or manual mode)
      const deductedItems = [];
      if (inventoryEnabled && materialsUsed.length > 0 && jobPlanLocationId) {
        for (const material of materialsUsed) {
          if (material.inventory_item_id && material.quantity_used > 0) {
            // Get current stock
            const { data: currentStock } = await supabaseData
              .from('inventory_stock')
              .select('id, quantity_on_hand')
              .eq('inventory_item_id', material.inventory_item_id)
              .eq('location_id', jobPlanLocationId)
              .single();

            if (currentStock) {
              const newQuantity = Math.max(0, (currentStock.quantity_on_hand || 0) - material.quantity_used);

              // Update stock
              await supabaseData
                .from('inventory_stock')
                .update({
                  quantity_on_hand: newQuantity,
                  updated_at: new Date().toISOString()
                })
                .eq('id', currentStock.id);

              // Create transaction record
              await supabaseData
                .from('inventory_transactions')
                .insert({
                  company_id: companyId,
                  inventory_item_id: material.inventory_item_id,
                  location_id: jobPlanLocationId,
                  job_id: job.id,
                  transaction_type: 'usage',
                  quantity_change: -material.quantity_used,
                  quantity_before: currentStock.quantity_on_hand || 0,
                  quantity_after: newQuantity,
                  notes: `Used on job ${job.job_number || job.id}`
                });

              deductedItems.push({ inventory_item_id: material.inventory_item_id, newQuantity });
            }
          }
        }

        // Fire-and-forget: check for critical stock alerts (SMS)
        if (deductedItems.length > 0) {
          fetch('/api/inventory-alert-check', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ companyId, items: deductedItems })
          }).catch(() => {}); // Non-blocking — don't fail job completion if alert fails
        }
      }

      // Update job status using RPC function (bypasses RLS issues)
      const { data: { user } } = await supabase.auth.getUser();

      const { data: rpcResult, error: rpcError } = await supabaseData
        .rpc('complete_job', {
          p_job_id: job.id,
          p_invoice_id: invoiceData.id,
          p_actual_total: calculateTotal(),
          p_user_id: user.id
        });

      let jobUpdateFailed = false;
      let jobUpdateErrorMsg = '';
      if (rpcError) {
        jobUpdateFailed = true;
        jobUpdateErrorMsg = rpcError.message || JSON.stringify(rpcError);
        console.error('[JobComplete] RPC error:', jobUpdateErrorMsg);
      } else if (!rpcResult?.success) {
        jobUpdateFailed = true;
        jobUpdateErrorMsg = rpcResult?.error || 'Unknown error';
        console.error('[JobComplete] RPC returned failure:', jobUpdateErrorMsg);
      } else {
        console.log('[JobComplete] Job status updated to completed via RPC');
        captureLocation(user?.id, companyId, 'job_complete');
      }

      setInvoice({
        ...invoiceData,
        customer_email: customer?.email
      });

      const balanceDue = calculateBalanceDue();

      // Determine if auto-charge should trigger:
      // 1. Per-job auto_charge_enabled flag, OR
      // 2. Company auto_bill_recurring setting + job is a recurring instance
      const isRecurringInstance = !!job.recurring_parent_id;
      const companyAutoBillRecurring = company?.auto_bill_recurring && isRecurringInstance;
      const shouldAutoCharge = job.auto_charge_enabled || companyAutoBillRecurring;

      // Try auto-charge if enabled and conditions are met
      if (balanceDue > 0 && shouldAutoCharge && customer?.default_payment_method_id) {
        setAutoCharging(true);
        try {
          // Determine receipt behavior for auto-billed recurring jobs
          let sendReceipt = true;
          if (companyAutoBillRecurring && !job.auto_charge_enabled) {
            const receiptMode = company?.auto_bill_receipt_mode || 'always';
            if (receiptMode === 'never') sendReceipt = false;
            else if (receiptMode === 'global') sendReceipt = !!company?.auto_email_receipts;
            // 'always' keeps sendReceipt = true
          }

          const chargeResponse = await fetch('/api/auto-charge', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              jobId: job.id,
              invoiceId: invoiceData.id,
              companyId: companyId,
              skipJobCheck: companyAutoBillRecurring,
              sendReceipt
            })
          });

          const chargeResult = await chargeResponse.json();

          if (chargeResponse.ok && chargeResult.success) {
            // Auto-charge succeeded
            const cardInfo = chargeResult.cardLast4 ? ` (${chargeResult.cardBrand || 'Card'} ****${chargeResult.cardLast4})` : '';
            showSuccess(`Auto-billed $${balanceDue.toFixed(2)}${cardInfo}`);
            if (sendReceipt && customer?.email) {
              showSuccess(`Receipt emailed to ${customer.email}`);
            }
            onComplete({ ...invoiceData, status: 'paid' });
            onClose();
            return;
          } else {
            // Auto-charge failed - show error and fall through to manual payment
            setAutoChargeError(chargeResult.error || 'Card charge failed');
          }
        } catch (chargeErr) {
          setAutoChargeError(chargeErr.message || 'Auto-charge request failed');
        } finally {
          setAutoCharging(false);
        }
      }

      // If balance due (and auto-charge didn't succeed), go to payment step
      if (balanceDue > 0) {
        setStep('payment');
        if (jobUpdateFailed) {
          showError(`Invoice created but job status failed: ${jobUpdateErrorMsg}`);
        } else if (!autoChargeError) {
          showSuccess(`Job completed! Balance due: $${balanceDue.toFixed(2)}`);
        }
      } else {
        if (jobUpdateFailed) {
          showError(`Invoice created but job status failed: ${jobUpdateErrorMsg}`);
        } else {
          showSuccess('Job completed and fully paid!');
        }
        // Auto-email receipt if enabled
        const emailedTo = await sendAutoReceipt({
          company, customer, amount: calculateTotal(),
          invoiceId: invoiceData.id, companyId
        });
        if (emailedTo) {
          showSuccess(`Receipt emailed to ${emailedTo}`);
        }
        onComplete(invoiceData);
        onClose();
      }

    } catch (error) {
      showError('Failed to complete job: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const proceedFromReview = () => {
    // If manual inventory deduction enabled and there are materials, show materials step
    if (inventoryEnabled && !inventoryAutoDeduct && materialsUsed.length > 0) {
      setStep('materials');
    } else if (chemicalTrackingEnabled) {
      setStep('chemicals');
    } else if (signatureMode === 'disabled') {
      completeJob();
    } else {
      setStep('signature');
    }
  };

  const proceedFromMaterials = () => {
    if (chemicalTrackingEnabled) {
      setStep('chemicals');
    } else if (signatureMode === 'disabled') {
      completeJob();
    } else {
      setStep('signature');
    }
  };

  const updateMaterialQuantity = (itemId, newQuantity) => {
    setMaterialsUsed(prev => prev.map(item =>
      item.id === itemId
        ? { ...item, quantity_used: Math.max(0, newQuantity) }
        : item
    ));
  };

  const proceedToSignature = () => {
    if (signatureMode === 'disabled') {
      completeJob();
    } else {
      setStep('signature');
    }
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.9)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 3000
      }}>
        <div style={{ color: '#fff', fontSize: '1.25rem' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: isMobile ? '0.5rem' : '1rem',
      backdropFilter: 'blur(4px)'
    }}>
      {/* Mobile CSS */}
      <style>{`
        @keyframes modalPopIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @media (max-width: 600px) {
          .job-completion-price-grid { grid-template-columns: 1fr 1fr !important; }
        }
      `}</style>
      <div style={{
        background: colors.bgCard,
        borderRadius: '16px',
        width: '100%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto',
        border: '3px solid #4ecca3',
        boxShadow: '0 0 40px rgba(78, 204, 163, 0.3), 0 25px 50px rgba(0,0,0,0.5)',
        animation: 'modalPopIn 0.2s ease-out'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.25rem',
          borderBottom: `1px solid ${colors.border}`,
          background: colors.bgInput,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          <div>
            <h2 style={{ margin: 0, color: '#4ecca3', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.25rem' }}>
              {step === 'chemicals' ? <Beaker size={22} /> : <CheckCircle size={22} />}
              {step === 'review' && 'Complete Job'}
              {step === 'chemicals' && 'Chemicals Used'}
              {step === 'signature' && 'Customer Signature'}
              {step === 'payment' && 'Collect Payment'}
            </h2>
            <p style={{ margin: '0.25rem 0 0', color: colors.textMuted, fontSize: '0.85rem' }}>
              {job.job_number} • {customer?.name || customer?.first_name || 'Customer'}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer' }}>
            <X size={24} />
          </button>
        </div>

        {/* STEP 1: Review Items */}
        {step === 'review' && (
          <div style={{ padding: '1.25rem' }}>
            {/* Line Items */}
            <div style={{ marginBottom: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h3 style={{ margin: 0, color: colors.text, fontSize: '1rem' }}>Invoice Items</h3>
                <button
                  onClick={() => setShowAddItem(true)}
                  style={{
                    background: colors.accent,
                    color: '#fff',
                    border: 'none',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem'
                  }}
                >
                  <Plus size={14} /> Add
                </button>
              </div>

              {jobItems.length === 0 ? (
                <div style={{
                  padding: '2rem',
                  background: colors.bgInput,
                  borderRadius: '8px',
                  textAlign: 'center',
                  color: colors.textMuted,
                  border: `1px dashed ${colors.border}`
                }}>
                  No items. Add work performed.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {jobItems.map(item => (
                    <div key={item.id} style={{
                      background: colors.bgInput,
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: item.is_additional ? `1px solid ${colors.accent}` : `1px solid ${colors.border}`
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: colors.text, fontWeight: '600', fontSize: '0.95rem' }}>
                            {item.name}
                            {item.is_additional && (
                              <span style={{
                                marginLeft: '0.5rem',
                                fontSize: '0.65rem',
                                background: colors.accent,
                                color: '#fff',
                                padding: '0.1rem 0.4rem',
                                borderRadius: '3px'
                              }}>ADDED</span>
                            )}
                          </div>
                          <div style={{ color: colors.textMuted, fontSize: '0.8rem', marginTop: '0.25rem' }}>
                            {item.quantity} × ${item.unit_price?.toFixed(2)}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ color: '#4ecca3', fontWeight: 'bold', fontSize: '1rem' }}>
                            ${item.total_price?.toFixed(2)}
                          </div>
                          <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.25rem' }}>
                            <button
                              onClick={() => setEditingItem(item)}
                              style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', padding: '0.25rem' }}
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => removeItem(item.id)}
                              style={{ background: 'none', border: 'none', color: '#ff6b6b', cursor: 'pointer', padding: '0.25rem' }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add Item Form */}
            {showAddItem && (
              <div style={{
                background: colors.bgInput,
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1.25rem',
                border: `2px solid ${colors.accent}`
              }}>
                <h4 style={{ margin: '0 0 0.75rem', color: colors.accent, fontSize: '0.9rem' }}>Add Item</h4>
                <input
                  type="text"
                  value={newItemName}
                  onChange={(e) => setNewItemName(e.target.value)}
                  placeholder="Description"
                  style={{
                    width: '100%',
                    padding: '0.6rem',
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.text,
                    marginBottom: '0.5rem'
                  }}
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input
                    type="number"
                    step="0.01"
                    value={newItemPrice}
                    onChange={(e) => setNewItemPrice(e.target.value)}
                    placeholder="Price"
                    style={{
                      padding: '0.6rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text
                    }}
                  />
                  <input
                    type="number"
                    min="1"
                    value={newItemQty}
                    onChange={(e) => setNewItemQty(parseInt(e.target.value) || 1)}
                    placeholder="Qty"
                    style={{
                      padding: '0.6rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text
                    }}
                  />
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={addItem} style={{
                    flex: 1, padding: '0.6rem', background: '#4ecca3', color: '#000',
                    border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer'
                  }}>Add</button>
                  <button onClick={() => setShowAddItem(false)} style={{
                    flex: 1, padding: '0.6rem', background: colors.bgHover, color: colors.text,
                    border: 'none', borderRadius: '6px', cursor: 'pointer'
                  }}>Cancel</button>
                </div>
              </div>
            )}

            {/* Totals */}
            {jobItems.length > 0 && (
              <div style={{
                background: colors.bgInput,
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1.25rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                  <span style={{ color: colors.textMuted }}>Subtotal:</span>
                  <span style={{ color: colors.text }}>${calculateSubtotal().toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                  <span style={{ color: colors.textMuted }}>Tax ({(getTaxRate() * 100).toFixed(1)}%):</span>
                  <span style={{ color: colors.text }}>${calculateTax().toFixed(2)}</span>
                </div>
                {quoteFeeCredit > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#22c55e', marginBottom: '0.4rem' }}>
                    <span>Quote Visit Credit:</span>
                    <span>-${quoteFeeCredit.toFixed(2)}</span>
                  </div>
                )}
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  paddingTop: '0.5rem', borderTop: `1px solid ${colors.border}`, marginBottom: '0.4rem'
                }}>
                  <span style={{ color: colors.text, fontWeight: 'bold' }}>Total:</span>
                  <span style={{ color: '#4ecca3', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    ${calculateTotal().toFixed(2)}
                  </span>
                </div>
                {depositDueAmount > 0 && !depositAlreadyPaid && (
                  <div
                    onClick={() => setManualDepositCollected(v => !v)}
                    style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      gap: '0.5rem', marginBottom: '0.4rem', cursor: 'pointer'
                    }}
                  >
                    <span style={{ color: colors.textMuted, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{
                        width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0,
                        border: `2px solid ${manualDepositCollected ? '#f39c12' : colors.border}`,
                        background: manualDepositCollected ? '#f39c12' : 'transparent',
                        color: '#fff', fontSize: '0.75rem', textAlign: 'center', lineHeight: '15px'
                      }}>{manualDepositCollected ? '✓' : ''}</span>
                      Deposit collected (cash/check)
                    </span>
                    <span style={{ color: colors.textMuted }}>-${depositDueAmount.toFixed(2)}</span>
                  </div>
                )}
                {getDepositPaid() > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#f39c12', marginBottom: '0.4rem' }}>
                    <span>Deposit {depositAlreadyPaid ? 'Paid' : 'Collected'}:</span>
                    <span>-${getDepositPaid().toFixed(2)}</span>
                  </div>
                )}
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  paddingTop: '0.5rem', borderTop: '2px solid #4ecca3'
                }}>
                  <span style={{ color: '#4ecca3', fontWeight: 'bold' }}>Balance Due:</span>
                  <span style={{ color: '#4ecca3', fontWeight: 'bold', fontSize: '1.25rem' }}>
                    ${calculateBalanceDue().toFixed(2)}
                  </span>
                </div>

                {/* Auto-charge indicator */}
                {(job.auto_charge_enabled || (company?.auto_bill_recurring && !!job.recurring_parent_id)) && customer?.default_payment_method_id && calculateBalanceDue() > 0 && (
                  <div style={{
                    marginTop: '0.75rem',
                    padding: '0.75rem',
                    background: '#4ecca320',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <Zap size={16} color="#4ecca3" />
                    <span style={{ color: '#4ecca3', fontSize: '0.85rem' }}>
                      Auto-charge {customer.card_brand?.toUpperCase() || 'card'} •••• {customer.card_last4} on completion
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.75rem', flexDirection: isMobile ? 'column' : 'row' }}>
              <button
                onClick={proceedFromReview}
                disabled={saving || jobItems.length === 0}
                style={{
                  flex: 1,
                  padding: '1rem',
                  background: (saving || jobItems.length === 0) ? colors.bgHover : 'linear-gradient(135deg, #4ecca3, #45b393)',
                  color: (saving || jobItems.length === 0) ? colors.textMuted : '#000',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: (saving || jobItems.length === 0) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <CheckCircle size={18} />
                {inventoryEnabled && !inventoryAutoDeduct && materialsUsed.length > 0
                  ? 'Continue to Materials'
                  : chemicalTrackingEnabled
                    ? 'Continue to Chemicals'
                    : (signatureMode === 'disabled' ? 'Complete Job' : 'Continue to Signature')}
              </button>
              <button onClick={onClose} style={{
                padding: '1rem',
                background: colors.bgInput,
                color: colors.text,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                cursor: 'pointer'
              }}>Cancel</button>
            </div>
          </div>
        )}

        {/* STEP 1b: Materials Used (manual deduction mode) */}
        {step === 'materials' && (
          <div style={{ padding: '1.25rem' }}>
            <p style={{ color: colors.textMuted, marginBottom: '1rem', fontSize: '0.9rem' }}>
              Review and adjust the materials used for this job. These quantities will be deducted from inventory.
            </p>

            {materialsUsed.length === 0 ? (
              <div style={{
                background: colors.bgInput,
                padding: '2rem',
                borderRadius: '8px',
                textAlign: 'center',
                color: colors.textMuted
              }}>
                <Package size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
                <p style={{ margin: 0 }}>No materials were planned for this job</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                {materialsUsed.map((material, index) => (
                  <div key={material.item_id} style={{
                    background: colors.bgInput,
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    border: `1px solid ${colors.border}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: colors.text, fontWeight: '500' }}>
                        {material.item_name}
                      </div>
                      <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                        Planned: {material.planned_quantity}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <button
                        onClick={() => updateMaterialQuantity(index, -1)}
                        disabled={material.used_quantity <= 0}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '6px',
                          border: 'none',
                          background: material.used_quantity <= 0 ? colors.bgHover : '#444',
                          color: material.used_quantity <= 0 ? colors.textMuted : '#fff',
                          cursor: material.used_quantity <= 0 ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Minus size={16} />
                      </button>
                      <div style={{
                        minWidth: '40px',
                        textAlign: 'center',
                        color: colors.text,
                        fontWeight: '600',
                        fontSize: '1.1rem'
                      }}>
                        {material.used_quantity}
                      </div>
                      <button
                        onClick={() => updateMaterialQuantity(index, 1)}
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '6px',
                          border: 'none',
                          background: '#444',
                          color: '#fff',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'flex-end',
              marginTop: '1.5rem',
              paddingTop: '1rem',
              borderTop: `1px solid ${colors.border}`
            }}>
              <button onClick={() => setStep('review')} style={{
                padding: '0.75rem 1.5rem',
                background: colors.bgInput,
                color: colors.text,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                cursor: 'pointer'
              }}>Back</button>
              <button onClick={proceedFromMaterials} style={{
                padding: '0.75rem 1.5rem',
                background: colors.accent,
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600'
              }}>Continue</button>
            </div>
          </div>
        )}

        {/* STEP 2: Chemicals (if enabled) */}
        {step === 'chemicals' && (
          <div style={{ padding: '1.25rem' }}>
            <p style={{ color: colors.textMuted, marginBottom: '1rem', fontSize: '0.9rem' }}>
              Record chemicals applied during this service. This information will appear on the receipt.
            </p>

            {/* Added Chemicals List */}
            {jobChemicals.length > 0 && (
              <div style={{ marginBottom: '1.25rem' }}>
                <h4 style={{ margin: '0 0 0.75rem', color: colors.text, fontSize: '0.95rem' }}>
                  Chemicals Applied ({jobChemicals.length})
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {jobChemicals.map(chem => (
                    <div key={chem.id} style={{
                      background: colors.bgInput,
                      padding: '0.75rem',
                      borderRadius: '8px',
                      border: `1px solid ${colors.border}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: colors.text, fontWeight: '600', marginBottom: '0.25rem' }}>
                          {chem.chemical_name}
                          {chem.epa_number && (
                            <span style={{ color: colors.textMuted, fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                              EPA: {chem.epa_number}
                            </span>
                          )}
                        </div>
                        <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>
                          {chem.quantity_used} {chem.unit}
                          {chem.application_area && ` • ${chem.application_area}`}
                          {chem.target_pest && ` • Target: ${chem.target_pest}`}
                        </div>
                        {chem.dilution_rate && (
                          <div style={{ color: colors.textMuted, fontSize: '0.8rem', marginTop: '0.25rem' }}>
                            Dilution: {chem.dilution_rate}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => removeChemical(chem.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ff6b6b',
                          cursor: 'pointer',
                          padding: '0.25rem'
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Chemical Form */}
            {showAddChemical ? (
              <div style={{
                background: colors.bgInput,
                padding: '1rem',
                borderRadius: '8px',
                marginBottom: '1.25rem',
                border: '2px solid #4ecca3'
              }}>
                <h4 style={{ margin: '0 0 0.75rem', color: '#4ecca3', fontSize: '0.9rem' }}>Add Chemical</h4>

                {/* Chemical Select */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.35rem', color: colors.textMuted, fontSize: '0.8rem' }}>
                    Chemical *
                  </label>
                  <select
                    value={selectedChemicalId}
                    onChange={(e) => {
                      setSelectedChemicalId(e.target.value);
                      const selected = chemicalLibrary.find(c => c.id === e.target.value);
                      if (selected) setChemicalUnit(selected.default_unit || 'oz');
                    }}
                    style={{
                      width: '100%',
                      padding: '0.6rem',
                      background: colors.bgCard,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '6px',
                      color: colors.text
                    }}
                  >
                    <option value="">Select chemical...</option>
                    {chemicalLibrary.map(c => (
                      <option key={c.id} value={c.id}>
                        {c.name} {c.epa_number ? `(EPA: ${c.epa_number})` : ''}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Quantity and Unit */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: colors.textMuted, fontSize: '0.8rem' }}>
                      Quantity *
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={chemicalQuantity}
                      onChange={(e) => setChemicalQuantity(e.target.value)}
                      placeholder="Amount"
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: colors.bgCard,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '6px',
                        color: colors.text
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: colors.textMuted, fontSize: '0.8rem' }}>
                      Unit
                    </label>
                    <select
                      value={chemicalUnit}
                      onChange={(e) => setChemicalUnit(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: colors.bgCard,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '6px',
                        color: colors.text
                      }}
                    >
                      <option value="oz">oz</option>
                      <option value="ml">ml</option>
                      <option value="gallon">gallon</option>
                      <option value="liter">liter</option>
                      <option value="lb">lb</option>
                      <option value="gram">gram</option>
                    </select>
                  </div>
                </div>

                {/* Application Details */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: colors.textMuted, fontSize: '0.8rem' }}>
                      Application Area
                    </label>
                    <input
                      type="text"
                      value={chemicalArea}
                      onChange={(e) => setChemicalArea(e.target.value)}
                      placeholder="e.g., Perimeter, Interior"
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: colors.bgCard,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '6px',
                        color: colors.text
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: colors.textMuted, fontSize: '0.8rem' }}>
                      Target Pest
                    </label>
                    <input
                      type="text"
                      value={chemicalTarget}
                      onChange={(e) => setChemicalTarget(e.target.value)}
                      placeholder="e.g., Ants, Roaches"
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: colors.bgCard,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '6px',
                        color: colors.text
                      }}
                    />
                  </div>
                </div>

                {/* Method and Dilution */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: colors.textMuted, fontSize: '0.8rem' }}>
                      Method
                    </label>
                    <select
                      value={chemicalMethod}
                      onChange={(e) => setChemicalMethod(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: colors.bgCard,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '6px',
                        color: colors.text
                      }}
                    >
                      <option value="">Select...</option>
                      <option value="Spray">Spray</option>
                      <option value="Bait">Bait</option>
                      <option value="Dust">Dust</option>
                      <option value="Granular">Granular</option>
                      <option value="Foam">Foam</option>
                      <option value="Aerosol">Aerosol</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: colors.textMuted, fontSize: '0.8rem' }}>
                      Dilution Rate
                    </label>
                    <input
                      type="text"
                      value={chemicalDilution}
                      onChange={(e) => setChemicalDilution(e.target.value)}
                      placeholder="e.g., 1 oz/gal"
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: colors.bgCard,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '6px',
                        color: colors.text
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button onClick={addChemical} style={{
                    flex: 1, padding: '0.6rem', background: '#4ecca3', color: '#000',
                    border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer'
                  }}>Add</button>
                  <button onClick={() => { setShowAddChemical(false); resetChemicalForm(); }} style={{
                    flex: 1, padding: '0.6rem', background: colors.bgHover, color: colors.text,
                    border: 'none', borderRadius: '6px', cursor: 'pointer'
                  }}>Cancel</button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowAddChemical(true)}
                style={{
                  width: '100%',
                  padding: '0.875rem',
                  background: colors.bgInput,
                  border: `2px dashed ${colors.border}`,
                  borderRadius: '8px',
                  color: colors.textMuted,
                  fontSize: '0.95rem',
                  cursor: 'pointer',
                  marginBottom: '1.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <Plus size={18} />
                Add Chemical
              </button>
            )}

            {/* No chemicals library message */}
            {chemicalLibrary.length === 0 && (
              <div style={{
                padding: '1rem',
                background: colors.bgInput,
                borderRadius: '8px',
                marginBottom: '1.25rem',
                textAlign: 'center'
              }}>
                <Beaker size={32} color={colors.textMuted} style={{ marginBottom: '0.5rem' }} />
                <p style={{ color: colors.textMuted, margin: 0, fontSize: '0.9rem' }}>
                  No chemicals in your library yet. Add chemicals in Settings to track them on jobs.
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.75rem', flexDirection: isMobile ? 'column' : 'row' }}>
              <button
                onClick={proceedToSignature}
                style={{
                  flex: 1,
                  padding: '1rem',
                  background: 'linear-gradient(135deg, #4ecca3, #45b393)',
                  color: '#000',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                <CheckCircle size={18} />
                {signatureMode === 'disabled' ? 'Complete Job' : 'Continue to Signature'}
              </button>
              <button onClick={() => setStep('review')} style={{
                padding: '1rem',
                background: colors.bgInput,
                color: colors.text,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                cursor: 'pointer'
              }}>Back</button>
            </div>
          </div>
        )}

        {/* STEP 3: Signature */}
        {step === 'signature' && (
          <div style={{ padding: '1.25rem' }}>
            <p style={{ color: colors.textMuted, marginBottom: '1rem', fontSize: '0.9rem' }}>
              {signatureMode === 'required'
                ? 'Customer signature is required to complete this job.'
                : 'Customer signature (optional)'}
            </p>

            <div style={{
              background: '#fff',
              borderRadius: '8px',
              padding: '0.5rem',
              marginBottom: '1rem'
            }}>
              <canvas
                ref={canvasRef}
                width={500}
                height={200}
                style={{
                  width: '100%',
                  height: '150px',
                  touchAction: 'none',
                  cursor: 'crosshair',
                  borderRadius: '4px'
                }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <button onClick={clearSignature} style={{
                flex: 1,
                padding: '0.75rem',
                background: colors.bgHover,
                color: colors.text,
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer'
              }}>Clear</button>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', flexDirection: isMobile ? 'column' : 'row' }}>
              <button
                onClick={completeJob}
                disabled={saving || (signatureMode === 'required' && !hasSignature)}
                style={{
                  flex: 1,
                  padding: '1rem',
                  background: (saving || (signatureMode === 'required' && !hasSignature)) ? colors.bgHover : 'linear-gradient(135deg, #4ecca3, #45b393)',
                  color: (saving || (signatureMode === 'required' && !hasSignature)) ? colors.textMuted : '#000',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  cursor: (saving || (signatureMode === 'required' && !hasSignature)) ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {saving ? 'Completing...' : (
                  <>
                    <FileText size={18} />
                    Complete & Create Invoice
                  </>
                )}
              </button>
              <button onClick={() => setStep('review')} disabled={saving} style={{
                padding: '1rem',
                background: colors.bgInput,
                color: colors.text,
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                cursor: 'pointer'
              }}>Back</button>
            </div>
          </div>
        )}

        {/* STEP 3: Payment Collection */}
        {step === 'payment' && invoice && (
          <div style={{ padding: '1.25rem' }}>
            <div style={{
              background: colors.bgInput,
              padding: '1.25rem',
              borderRadius: '8px',
              marginBottom: '1.25rem',
              textAlign: 'center'
            }}>
              <div style={{ color: '#4ecca3', fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                Invoice {invoice.invoice_number}
              </div>
              <div style={{ color: colors.text, fontSize: '2rem', fontWeight: 'bold' }}>
                ${calculateBalanceDue().toFixed(2)}
              </div>
              <div style={{ color: colors.textMuted, fontSize: '0.85rem' }}>Balance Due</div>
            </div>

            {/* Auto-charge error message */}
            {autoChargeError && (
              <div style={{
                background: '#ff6b6b20',
                border: '1px solid #ff6b6b',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1.25rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem'
              }}>
                <AlertCircle size={20} color="#ff6b6b" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <div style={{ color: '#ff6b6b', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                    Auto-charge failed
                  </div>
                  <div style={{ color: colors.text, fontSize: '0.9rem' }}>
                    {autoChargeError}
                  </div>
                  <div style={{ color: colors.textMuted, fontSize: '0.85rem', marginTop: '0.5rem' }}>
                    Please collect payment using one of the options below.
                  </div>
                </div>
              </div>
            )}

            {(company?.stripe_connect_id && company?.stripe_charges_enabled) || company?.square_merchant_id ? (
              <>
                {/* Terminal Payment Option - Card Present (Stripe Terminal only) */}
                {company?.terminal_enabled && company?.stripe_connect_id && (
                  <>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <TerminalPaymentButton
                        invoice={invoice}
                        companyId={companyId}
                        connectedAccountId={company.stripe_connect_id}
                        onSuccess={() => {
                          onComplete(invoice);
                          onClose();
                        }}
                        size="large"
                      />
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      margin: '0.75rem 0',
                      color: colors.textMuted,
                      fontSize: '0.85rem'
                    }}>
                      <div style={{ flex: 1, height: '1px', background: colors.bgHover }} />
                      <span>or pay online</span>
                      <div style={{ flex: 1, height: '1px', background: colors.bgHover }} />
                    </div>
                  </>
                )}

                {/* Online Payment Option - Card Not Present */}
                <div style={{ marginBottom: '1rem' }}>
                  <PayInvoiceButton
                    invoice={invoice}
                    companyId={company?.id}
                    companyStripeAccountId={company.stripe_connect_id}
                    companySquareMerchantId={company.square_merchant_id}
                    companySquareLocationId={company.square_location_id}
                    companyName={company.name}
                    size="large"
                  />
                </div>
                <button
                  onClick={() => {
                    onComplete(invoice);
                    onClose();
                  }}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: colors.bgInput,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.95rem'
                  }}
                >
                  Collect Payment Later
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => {
                    onClose();
                    if (onNavigate) onNavigate('settings', { section: 'payment' });
                  }}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: 'linear-gradient(135deg, #635bff 0%, #8b5cf6 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.95rem',
                    fontWeight: 'bold',
                    marginBottom: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  💳 Set Up Payments to Collect
                </button>
                <button
                  onClick={() => {
                    onComplete(invoice);
                    onClose();
                  }}
                  style={{
                    width: '100%',
                    padding: '1rem',
                    background: colors.bgInput,
                    color: colors.textMuted,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.95rem'
                  }}
                >
                  Collect Payment Manually
                </button>
              </>
            )}
          </div>
        )}

        {/* Edit Item Modal */}
        {editingItem && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 4000, padding: '1rem'
          }}>
            <div style={{
              background: colors.bgInput,
              borderRadius: '12px',
              padding: '1.25rem',
              maxWidth: '400px',
              width: '100%'
            }}>
              <h3 style={{ margin: '0 0 1rem', color: colors.text }}>Edit Item</h3>
              <input
                type="text"
                value={editingItem.name}
                onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  color: colors.text,
                  marginBottom: '0.5rem'
                }}
              />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
                <input
                  type="number"
                  step="0.01"
                  value={editingItem.unit_price}
                  onChange={(e) => {
                    const price = parseFloat(e.target.value) || 0;
                    setEditingItem({
                      ...editingItem,
                      unit_price: price,
                      total_price: price * editingItem.quantity
                    });
                  }}
                  style={{
                    padding: '0.75rem',
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.text
                  }}
                />
                <input
                  type="number"
                  min="1"
                  value={editingItem.quantity}
                  onChange={(e) => {
                    const qty = parseInt(e.target.value) || 1;
                    setEditingItem({
                      ...editingItem,
                      quantity: qty,
                      total_price: editingItem.unit_price * qty
                    });
                  }}
                  style={{
                    padding: '0.75rem',
                    background: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    color: colors.text
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => updateItem(editingItem.id, {
                    name: editingItem.name,
                    unit_price: editingItem.unit_price,
                    quantity: editingItem.quantity,
                    total_price: editingItem.total_price
                  })}
                  style={{
                    flex: 1, padding: '0.75rem', background: '#4ecca3', color: '#000',
                    border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer'
                  }}
                >Save</button>
                <button onClick={() => setEditingItem(null)} style={{
                  flex: 1, padding: '0.75rem', background: colors.bgHover, color: colors.text,
                  border: 'none', borderRadius: '6px', cursor: 'pointer'
                }}>Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
